import type { Identifiers } from '@atlaskit/feature-gate-js-client';
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '../stores/redux.store';
import { getFfsId } from '../utilities/ffs-id';
import { FeatureGatesInit } from './FeatureGatesInit';
import { useAnalyticsAnonymousId } from '../utilities/analytics/analytics-web-client';
import { ConfigCollection } from '@atlaskit/web-config-client';
import Spinner from '@atlaskit/spinner';
import { useIntl } from 'react-intl';
import {
  DynamicConfigsContext,
  initializeDynamicConfigs,
} from '../utilities/feature-flags/dynamic-config';
import { captureException } from '../utilities/analytics/error-reporting';
import { resolveAnonIdOverride } from '../utilities/anonIdQueryParam';

type FeatureGateProviderProps = {
  children: ReactNode;
};

export const FeatureGateProvider = ({ children }: FeatureGateProviderProps) => {
  const [isFeatureGatesClientReady, setIsFeatureGatesClientReady] = useState(false);
  const [dynamicConfigsClient, setDynamicConfigsCollection] = useState<
    ConfigCollection | null | undefined
  >(undefined);

  const tenantId = useAppSelector(state => state.context?.tenantCloudId);
  const atlassianAccountId = useAppSelector(state => state.context?.userId);
  const ffsId = getFfsId();
  const { formatMessage } = useIntl();
  const analyticsAnonymousId = useAnalyticsAnonymousId();

  // An `anonId` query parameter may carry an anonymous identity from an
  // originating surface. When present, we instantiate the Statsig client (feature
  // gates + dynamic configs) with that id so bucketing lines up across surfaces.
  // The override is persisted for the tab session so that server-built redirects
  // that drop the param (e.g. signup → OTP → Welcome) don't re-bucket the user.
  const anonIdOverride = resolveAnonIdOverride(window.location.search);
  const effectiveAnonymousId = anonIdOverride ?? analyticsAnonymousId;

  const featureGatesIdentifiers: Identifiers = useMemo(
    () => ({
      tenantId,
      atlassianAccountId,
      randomizationId: ffsId,
      analyticsAnonymousId: effectiveAnonymousId,
    }),
    [tenantId, atlassianAccountId, ffsId, effectiveAnonymousId]
  );

  useEffect(() => {
    initializeDynamicConfigs(effectiveAnonymousId).then(dynamicConfigs =>
      setDynamicConfigsCollection(dynamicConfigs)
    );
  }, [effectiveAnonymousId]);

  const onFeatureGatesReady = useCallback((err?: Error | undefined) => {
    setIsFeatureGatesClientReady(true);
    captureStatsigFeatureGateError(err);
  }, []);

  const areClientsReady = isFeatureGatesClientReady && !!dynamicConfigsClient;

  return (
    <>
      <FeatureGatesInit identifiers={featureGatesIdentifiers} onReady={onFeatureGatesReady} />

      {!areClientsReady && (
        <Spinner
          label={formatMessage({
            id: 'statsig.clients.loading',
            defaultMessage: 'Loading feature flags...',
            description:
              'Label text for loading spinner when Statsig clients are being initialized',
          })}
        />
      )}

      {areClientsReady && (
        <DynamicConfigsContext.Provider value={dynamicConfigsClient}>
          {children}
        </DynamicConfigsContext.Provider>
      )}
    </>
  );
};

const captureStatsigFeatureGateError = (err?: Error | undefined) => {
  if (!err) return;

  captureException(err, undefined, { statsigFeatureGate: 'statsig_feature_gate_client_init' });
};
